import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, MapPin, Heart, Star, Home, Building, Building2, Key, Briefcase, Bed, Bath, Maximize, Toilet, Shield, Users, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, { FILTERS_INITIAL_STATE, PropertyFiltersState, applyFilters } from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

interface Property {
  id: string
  seo_title: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  city?: string
  amenities?: string[]
  featured_image?: string
}

interface ConfiableTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const ConfiableTemplate: React.FC<ConfiableTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0]
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }
    fetchProperties()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`
  }

  const getSpecialtyIcon = (specialty: string) => {
    const lower = specialty.toLowerCase()
    if (lower.includes('casa')) return Home
    if (lower.includes('depa') || lower.includes('departamento')) return Building
    if (lower.includes('familia')) return Users
    if (lower.includes('renta')) return Key
    if (lower.includes('oficina')) return Briefcase
    return Building2
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)
  const showPhone = false
  const showWhatsapp = false

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFF8F0' }}>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #7C3A1E 0%, #C4622D 50%, #E8843A 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full border-4 border-white" />
          <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full border-4 border-white" />
          <div className="absolute top-1/2 left-1/3 w-20 h-20 rounded-full border-2 border-white" />
        </div>

        <div className="relative container mx-auto px-6 py-20">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            {/* Texto */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <Heart size={16} className="text-orange-200" />
                <span className="text-orange-100 text-sm font-medium">Tu asesor de confianza</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                {agent.name}
              </h1>
              {agent.bio && (
                <p className="text-xl text-orange-100 leading-relaxed">
                  {agent.bio}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-4">
                {(showPhone || showWhatsapp) && agent.phone && (
                  <>
                    {showPhone && (
                      <a
                        href={`tel:${agent.phone}`}
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-orange-700 font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        <Phone size={20} />
                        {agent.phone}
                      </a>
                    )}
                    {showWhatsapp && (
                      <a
                        href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#1fb855] text-white font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </>
                )}
              </div>

              {/* Social Media */}
              {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                <div className="flex gap-3">
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-white/20 hover:bg-white/40 rounded-xl transition-all duration-300 hover:scale-110">
                      <Facebook className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-white/20 hover:bg-white/40 rounded-xl transition-all duration-300 hover:scale-110">
                      <Instagram className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-white/20 hover:bg-white/40 rounded-xl transition-all duration-300 hover:scale-110">
                      <Linkedin className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {agent.social_media.tiktok && (
                    <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-white/20 hover:bg-white/40 rounded-xl transition-all duration-300 hover:scale-110">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-white/20 hover:bg-white/40 rounded-xl transition-all duration-300 hover:scale-110">
                      <Youtube className="w-5 h-5 text-white" />
                    </a>
                  )}
                </div>
              )}
            </motion.div>

            {/* Avatar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute -inset-3 bg-white/20 rounded-full blur-lg" />
                {agent.avatar_url ? (
                  <img
                    src={agent.avatar_url}
                    alt={agent.name}
                    className="relative w-72 h-72 lg:w-80 lg:h-80 rounded-full object-cover border-4 border-white/40 shadow-2xl"
                  />
                ) : (
                  <div className="relative w-72 h-72 lg:w-80 lg:h-80 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/40">
                    <span className="text-8xl font-bold text-white/50">{agent.name.charAt(0)}</span>
                  </div>
                )}
                <motion.div
                  animate={{ y: [-8, 8, -8] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -bottom-4 -right-4 bg-white rounded-2xl px-4 py-3 shadow-xl"
                >
                  <div className="flex items-center gap-2">
                    <Heart size={18} className="text-orange-500" />
                    <div>
                      <div className="text-lg font-bold text-orange-700">{properties.length}</div>
                      <div className="text-xs text-orange-500">hogares</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Propiedades */}
      {properties.length > 0 && (
        <section className="py-20" style={{ backgroundColor: '#FFF8F0' }}>
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-4"
            >
              <h2 className="text-4xl font-bold mb-3" style={{ color: '#7C3A1E' }}>
                Hogares con Alma
              </h2>
              <div className="w-16 h-1 mx-auto rounded-full" style={{ backgroundColor: '#C4622D' }} />

              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="amber"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
              {paginatedProperties.map((property, index) => (
                <motion.article
                  key={property.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title)}
                  className="group cursor-pointer bg-white rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-orange-100"
                >
                  <div className="relative h-52 overflow-hidden">
                    {property.featured_image ? (
                      <img
                        src={property.featured_image}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FDE8D8, #FBBF89)' }}>
                        <Home size={48} className="text-orange-300" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-white/95 px-3 py-1 rounded-full text-xs font-bold capitalize" style={{ color: '#C4622D' }}>
                      {property.purpose}
                    </div>
                    <div className="absolute top-3 right-3 text-white px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#C4622D' }}>
                      {formatPrice(property.price, property.currency)}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-1 group-hover:text-orange-600 transition-colors line-clamp-1" style={{ color: '#7C3A1E' }}>
                      {property.title}
                    </h3>
                    <p className="text-sm flex items-center gap-1 mb-3" style={{ color: '#C4622D' }}>
                      <MapPin size={13} />
                      {property.zone}{property.city ? `, ${property.city}` : ''}
                    </p>
                    {property.description && (
                      <p className="text-gray-500 text-sm line-clamp-2 mb-3">{property.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-gray-400 text-sm border-t border-orange-50 pt-3">
                      {property.bedrooms && (
                        <span className="flex items-center gap-1"><Bed size={14} /> {property.bedrooms}</span>
                      )}
                      {property.bathrooms && (
                        <span className="flex items-center gap-1"><Bath size={14} /> {property.bathrooms}</span>
                      )}
                      {property.half_bathrooms > 0 && (
                        <span className="flex items-center gap-1"><Toilet size={14} /> {property.half_bathrooms}</span>
                      )}
                      {property.area_m2 && (
                        <span className="flex items-center gap-1"><Maximize size={14} /> {property.area_m2}m²</span>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            <PropertyPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
              colorSchemeKey="amber"
            />
          </div>
        </section>
      )}

      {/* Especialidades */}
      {agent.specialties?.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-3" style={{ color: '#7C3A1E' }}>Lo que hago mejor</h2>
              <div className="w-16 h-1 mx-auto rounded-full" style={{ backgroundColor: '#C4622D' }} />
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-5xl mx-auto">
              {agent.specialties.map((specialty, index) => {
                const IconComponent = getSpecialtyIcon(specialty)
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    viewport={{ once: true }}
                    className="bg-orange-50 hover:bg-orange-100 rounded-2xl p-6 text-center transition-all duration-300 hover:scale-105 border border-orange-100"
                  >
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FDE8D8' }}>
                      <IconComponent size={26} style={{ color: '#C4622D' }} />
                    </div>
                    <h3 className="font-semibold capitalize text-sm" style={{ color: '#7C3A1E' }}>{specialty}</h3>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* Zonas */}
      {agent.zones?.length > 0 && (
        <section className="py-20" style={{ backgroundColor: '#FFF8F0' }}>
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-3" style={{ color: '#7C3A1E' }}>Dónde trabajo</h2>
              <div className="w-16 h-1 mx-auto rounded-full" style={{ backgroundColor: '#C4622D' }} />
            </motion.div>
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {agent.zones.map((zone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-sm border border-orange-100 hover:shadow-md transition-all"
                >
                  <MapPin size={16} style={{ color: '#C4622D' }} />
                  <span className="font-medium" style={{ color: '#7C3A1E' }}>{zone}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #7C3A1E, #C4622D)' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} className="fill-orange-300 text-orange-300" />
              ))}
            </div>
            <h2 className="text-4xl font-bold text-white">
              Tu hogar ideal te está esperando
            </h2>
            <p className="text-orange-100 text-lg leading-relaxed">
              Juntos encontraremos el espacio perfecto para ti y tu familia.
              {properties.length > 0 && ` ${properties.length} propiedades disponibles ahora mismo.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              {agent.phone && (
                <>
                  <a href={`tel:${agent.phone}`}
                    className="flex items-center justify-center gap-3 px-8 py-4 bg-white font-bold rounded-2xl transition-all hover:scale-105 shadow-lg" style={{ color: '#C4622D' }}>
                    <Phone size={20} /> Llamar ahora
                  </a>
                  <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#1fb855] text-white font-bold rounded-2xl transition-all hover:scale-105 shadow-lg">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <ChatWidget
        agentName={agent.name}
        agentSubdomain={window.location.hostname.split('.')[0]}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default ConfiableTemplate
